import { useMemo } from 'react';
import {
    Edit,
    FunctionField,
    NumberField,
    SimpleForm,
    TextField,
    TextInput,
    TopToolbar,
    maxLength,
    minLength,
    regex,
    required,
    usePermissions,
    useRecordContext,
    useTranslate,
    useUnique,
} from 'react-admin';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { OnlyBulkDeleteActionButton } from '@/components/global/buttons/bulkButtons.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { DetailsShow } from '@/components/global/drawers/DetailsShow.jsx';
import { DetailsActionsPopover } from '@/components/global/buttons/DetailsActionsPopover.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { DeviceModelStatisticList } from '@/components/analytics/statisticList.jsx';

const getFilters = (isMobile, translate) => [
    <SearchBar key='filter_search' source='value' label={translate('common.search')} alwaysOn />,
    <TextInput key='filter_code' source='code_like' label={translate('resources.deviceModel.fields.code')} alwaysOn={isMobile} />,
    <TextInput key='filter_name' source='name_like' label={translate('resources.deviceModel.fields.name')} alwaysOn={isMobile} />,
    <TextInput key='filter_description' source='description_like' label={translate('resources.deviceModel.fields.description')} alwaysOn={isMobile} />,
];

export const DeviceModelList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);
    const canShow = canViewDetails('device-model', permissions);

    if (isLoading) return null;

    return (
        <CustomList
            filters={filters}
            sort={{ field: 'code', order: 'ASC' }}
            actions={<ListActions />}
            title={translate('resources.deviceModel.labels.allModels')}
            addLastColumnShadow={true}
            empty={false}
        >
            <>
                <DeviceModelStatisticList />
                <CustomDatagridConfigurable
                    bulkActionButtons={permissions.isAdmin ? <OnlyBulkDeleteActionButton /> : false}
                    rowClick={
                        canShow
                            ? id => {
                                  openDrawer(id, 'device-model-show');
                                  return false;
                              }
                            : false
                    }
                >
                    <TextField source='code' label={translate('resources.deviceModel.fields.code')} />
                    <TextField source='name' label={translate('resources.deviceModel.fields.name')} />
                    <TextField source='description' label={translate('resources.deviceModel.fields.description')} />
                    <NumberField source='deviceCount' label={translate('resources.deviceModel.fields.deviceCount')} />
                    {canShow || permissions.isAdmin ? (
                        <FunctionField
                            label={translate('common.options')}
                            render={() => (
                                <Box onClick={event => event.stopPropagation()}>
                                    <MoreActionsPopover />
                                </Box>
                            )}
                        />
                    ) : (
                        <></>
                    )}
                </CustomDatagridConfigurable>
            </>
        </CustomList>
    );
};

export const DeviceModelCreate = ({ handleClose }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.deviceModel.labels.create')}>
            <CustomCreate handleClose={handleClose}>
                <DeviceModelForm />
            </CustomCreate>
        </RightDrawer>
    );
};

const DeviceModelDetailsActions = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();

    if (!permissions?.isAdmin || !record?.id) {
        return null;
    }

    return (
        <DetailsActionsPopover>
            <CustomEditModalButton onOpen={() => openDrawer(record.id, 'device-model-edit')} label={translate('resources.deviceModel.labels.edit')} />
        </DetailsActionsPopover>
    );
};

export const DeviceModelShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <DetailsShow
            handleClose={handleClose}
            elementId={elementId}
            resource='device-model'
            title={translate('resources.deviceModel.labels.details')}
            canView={permissions => canViewDetails('device-model', permissions)}
            actions={<DeviceModelDetailsActions />}
            showChangeHistory
            historyFieldsSource='resources.deviceModel.fields'
        >
            <TextField source='code' label={translate('resources.deviceModel.fields.code')} />
            <TextField source='name' label={translate('resources.deviceModel.fields.name')} />
            <TextField source='description' label={translate('resources.deviceModel.fields.description')} />
        </DetailsShow>
    );
};

export const DeviceModelEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.deviceModel.labels.edit')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <DeviceModelForm elementId={elementId} />
            </Edit>
        </RightDrawer>
    );
};

const DeviceModelForm = ({ elementId }) => {
    const translate = useTranslate();
    const unique = useUnique(elementId ? { filter: { id_neq: elementId } } : undefined);

    return (
        <SimpleForm toolbar={<EditToolbar />}>
            <TextInput
                source='code'
                label={translate('resources.deviceModel.fields.code')}
                validate={[
                    required(translate('common.validation.required')),
                    regex(/^\d{3}$/, translate('resources.deviceModel.validation.invalidCode')),
                    unique(),
                ]}
                fullWidth
            />
            <TextInput
                source='name'
                label={translate('resources.deviceModel.fields.name')}
                validate={[
                    required(translate('common.validation.required')),
                    minLength(3, translate('common.validation.minLength', { count: 3 })),
                    maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                ]}
                fullWidth
            />
            <TextInput
                source='description'
                label={translate('resources.deviceModel.fields.description')}
                validate={[maxLength(300, translate('common.validation.maxLength', { count: 300 }))]}
                multiline
                fullWidth
            />
        </SimpleForm>
    );
};

const MoreActionsPopover = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const { isLoading, permissions } = usePermissions();
    const canShow = canViewDetails('device-model', permissions);

    if (!record || isLoading || (!canShow && !permissions.isAdmin)) return null;

    return (
        <CustomMorePopover record={record}>
            {canShow && (
                <CustomShowModalButton onOpen={() => openDrawer(record.id, 'device-model-show')} label={translate('resources.deviceModel.labels.show')} />
            )}
            {permissions.isAdmin && (
                <CustomEditModalButton onOpen={() => openDrawer(record.id, 'device-model-edit')} label={translate('resources.deviceModel.labels.edit')} />
            )}
        </CustomMorePopover>
    );
};

const ListActions = () => {
    const translate = useTranslate();

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            <CustomToolbarActionsPopover>
                <CustomCreateButton label={translate('resources.deviceModel.labels.create')} />
            </CustomToolbarActionsPopover>
        </TopToolbar>
    );
};

DeviceModelCreate.propTypes = {
    handleClose: PropTypes.func.isRequired,
};

DeviceModelShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DeviceModelEdit.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
};

DeviceModelForm.propTypes = {
    elementId: PropTypes.string,
};
